// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Modal, Pressable, StyleSheet, Text, View } from 'react-native';
import { FontAwesome } from '@expo/vector-icons';

import { useI18n } from '@/i18n';
import type { UserSession } from '@/app/user-context';
import { useDebugTraceState } from '@/stores/debugTraceState';

type DebugModalProps = {
  open: boolean;
  onClose: () => void;
  user: UserSession | null;
  playerId: string | null;
  roundId: string | null;
  tourId: string | null;
  roundName: string | null;
  tourName: string | null;
  loading: boolean;
  onDisable: () => void;
};

export const DebugModal = ({
  open,
  onClose,
  user,
  playerId,
  roundId,
  tourId,
  roundName,
  tourName,
  loading,
  onDisable,
}: DebugModalProps) => {
  const { t } = useI18n();
  const { holeViewTraceId, holeScoreTraceId } = useDebugTraceState();
  const canCopy = typeof navigator !== 'undefined' && !!navigator.clipboard?.writeText;

  const handleCopy = async () => {
    if (!canCopy) return;
    const lines = [
      `userId=${user?.id ?? ''}`,
      `playerId=${playerId ?? ''}`,
      `roundId=${roundId ?? ''}`,
      `tourId=${tourId ?? ''}`,
      `holeViewTraceId=${holeViewTraceId ?? ''}`,
      `holeScoreTraceId=${holeScoreTraceId ?? ''}`,
    ];
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
    } catch {
      // Ignore clipboard failures.
    }
  };
  const handleCopyValue = async (value: string | null | undefined) => {
    if (!canCopy) return;
    try {
      await navigator.clipboard.writeText(value ?? '');
    } catch {
      // Ignore clipboard failures.
    }
  };
  const renderRow = (label: string, value: string | null | undefined, copyKey?: string) => (
    <View style={styles.row} key={label}>
      <View style={styles.rowHeader}>
        <Text style={styles.label}>{label}</Text>
        {canCopy && copyKey ? (
          <Pressable
            style={styles.copyButtonInline}
            onPress={() => handleCopyValue(value)}
            accessibilityLabel={t('debug.copyIdLabel', { label })}
          >
            <FontAwesome name="copy" size={12} color="#c7d7ff" />
          </Pressable>
        ) : null}
      </View>
      <Text style={styles.value}>{value ?? t('debug.unknown')}</Text>
    </View>
  );

  return (
    <Modal transparent visible={open} animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.backdrop} onPress={onClose}>
        <View style={styles.card}>
          <View style={styles.headerRow}>
            <Text style={styles.title}>{t('debug.title')}</Text>
            {canCopy ? (
              <Pressable style={styles.copyButton} onPress={handleCopy} accessibilityLabel={t('debug.copyIdsLabel')}>
                <FontAwesome name="copy" size={16} color="#c7d7ff" />
              </Pressable>
            ) : null}
          </View>
          {renderRow(t('debug.userLabel'), user?.forename ?? t('debug.unknown'))}
          {renderRow(t('debug.userIdLabel'), user?.id ?? t('debug.unknown'), 'userId')}
          {renderRow(t('debug.playerIdLabel'), playerId ?? t('debug.unknown'), 'playerId')}
          {renderRow(t('debug.roundLabel'), roundId ?? t('debug.unknown'), 'roundId')}
          {renderRow(t('debug.roundNameLabel'), roundName ?? t('debug.unknown'))}
          {renderRow(t('debug.tourLabel'), tourId ?? t('debug.unknown'), 'tourId')}
          {renderRow(t('debug.tourNameLabel'), tourName ?? t('debug.unknown'))}
          {renderRow(t('debug.holeViewTraceLabel'), holeViewTraceId ?? t('debug.traceNone'), 'holeViewTraceId')}
          {renderRow(t('debug.holeScoreTraceLabel'), holeScoreTraceId ?? t('debug.traceNone'), 'holeScoreTraceId')}
          {loading ? <Text style={styles.meta}>{t('debug.loading')}</Text> : null}
          <Pressable style={styles.close} onPress={onClose}>
            <Text style={styles.closeText}>{t('debug.close')}</Text>
          </Pressable>
          <Pressable style={styles.disable} onPress={onDisable}>
            <Text style={styles.disableText}>{t('debug.disable')}</Text>
          </Pressable>
        </View>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(7,12,24,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    minWidth: 280,
    width: '86%',
    maxWidth: 360,
    padding: 18,
    borderRadius: 12,
    backgroundColor: '#0b1224',
    borderWidth: 1,
    borderColor: '#2a3a6b',
    shadowColor: '#000',
    shadowOpacity: 0.35,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 6 },
    elevation: 8,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    color: '#f4f6fb',
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 10,
  },
  copyButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    marginTop: -2,
  },
  copyButtonInline: {
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  row: {
    marginBottom: 8,
  },
  rowHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  label: {
    color: '#9fb4d9',
    fontSize: 12,
    fontWeight: '600',
  },
  value: {
    color: '#e9f0ff',
    fontSize: 13,
    marginTop: 2,
  },
  meta: {
    color: '#8a94b2',
    fontSize: 12,
    marginTop: 4,
  },
  close: {
    alignSelf: 'flex-end',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
    backgroundColor: '#1b2544',
    marginTop: 6,
  },
  closeText: {
    color: '#e6edff',
    fontWeight: '600',
  },
  disable: {
    alignSelf: 'flex-start',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#f1c27b',
    marginTop: 10,
  },
  disableText: {
    color: '#f1c27b',
    fontWeight: '600',
  },
});
